import { useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import styled from 'styled-components';
import { Theme } from '../theme';
import { DOWN_KEY, ENTER_KEY, ESCAPE_KEY, SPACE_KEY, TAB_KEY } from '../../utils/keys';
import useTheme from '../../utils/useTheme';
import { Icon } from '../Icon/Icon';
import {
  ISelectMiniMenuPlacement,
  SelectMiniBlanket,
  SelectMiniButton,
  SelectMiniDropdown,
  SelectMiniDropdownIndicator,
  SelectMiniMenu,
  SelectMiniStyles,
} from '../SelectMini/SelectMini';
import { Checkbox } from '../Checkbox/Checkbox';
import { sortOptions } from '../../utils/sorting';

const SelectMiniMultiStyling = (theme: Theme) => ({
  ...SelectMiniStyles(theme),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    ...SelectMiniStyles(theme).option,
    backgroundColor: isFocused ? theme.vars.newColor.blue4 : isSelected ? theme.vars.newColor.blue1 : null,

    '.form': {
      label: {
        color: isSelected
          ? isFocused
            ? theme.vars.newColor.darkGS1
            : theme.vars.newColor.white
          : theme.vars.newColor.darkGS1,
        pointerEvents: 'none',
      },
    },
  }),
});

const StyledSelect = styled(Select)``;

export interface ISelectMiniMultiOption {
  label: string;
  value: string;
}

interface ISelectMiniMultiProps {
  /** Label for the dropdown menu. */
  label?: string;
  /** aria-label for the dropdown menu. */
  ariaLabel: string;
  /** Available options  */
  options: ISelectMiniMultiOption[];
  /** Selected value(s) */
  value?: ISelectMiniMultiOption[];
  /** Default value(s) */
  defaultValue?: ISelectMiniMultiOption[];
  /** Invoked on change select option */
  onChange: (value: ISelectMiniMultiOption[]) => void;
  /** Disables select element */
  disabled?: boolean;
  /** Width of select menu */
  menuWidth?: string;
  /** Placeholder when no options are selected. Defaults to 'Select...' */
  placeholder?: string;
  /** Menu placement */
  menuPlacement?: ISelectMiniMenuPlacement;
  /** Whether or not the menu is open on load. Defaults to false. */
  defaultMenuIsOpen?: boolean;
  /** Alphabetically sort the options by the label name.  */
  isSorted?: boolean;
}

const Option = props => {
  return (
    <components.Option {...props}>
      <Checkbox
        id={`checkbox-${props.value.replace(/\s+/g, '-')}`}
        label={props.label}
        checked={props.isSelected}
        onChange={() => null}
      />
    </components.Option>
  );
};

export const SelectMiniMulti = ({
  ariaLabel,
  defaultMenuIsOpen,
  defaultValue,
  disabled,
  isSorted,
  label,
  menuPlacement,
  menuWidth,
  onChange,
  options,
  placeholder = 'Select...',
  value,
}: ISelectMiniMultiProps) => {
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(defaultMenuIsOpen);
  const [selectedItems, setSelectedItems] = useState(value ? value : null);

  useEffect(() => {
    setSelectedItems(value ? value : null);
  }, [value]);

  const handleOnChange = (items: ISelectMiniMultiOption[]) => {
    setSelectedItems(items ? items : null);
  };

  const handleOnClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!disabled && (e.keyCode === DOWN_KEY || e.keyCode === ENTER_KEY || e.keyCode === SPACE_KEY)) {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleBlanketClick = () => {
    setIsOpen(false);
  };

  const handleOnBlur = () => {
    onChange(selectedItems);
  };

  const handleOnKeyDown = (e: React.KeyboardEvent) => {
    if (e.keyCode === ESCAPE_KEY) {
      setIsOpen(false);
      onChange(selectedItems);
    } else if (e.keyCode === TAB_KEY) {
      e.preventDefault();
    }
  };

  return (
    <SelectMiniDropdown>
      <SelectMiniButton
        data-testid="SelectMiniButton"
        disabled={disabled}
        onClick={handleOnClick}
        onKeyDownCapture={handleKeyDown}
      >
        <span data-testid="SelectMiniValue">{label ? label : placeholder}</span>

        <SelectMiniDropdownIndicator>
          <Icon icon="angle-down" color={theme.vars.newColor.darkGS1} />
        </SelectMiniDropdownIndicator>
      </SelectMiniButton>

      {!disabled && isOpen && (
        <SelectMiniMenu data-testid="SelectMiniMenu" menuPlacement={menuPlacement} menuWidth={menuWidth}>
          <StyledSelect
            aria-label={ariaLabel}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={true}
            backspaceRemovesValue={false}
            className="select-container"
            classNamePrefix="select"
            closeMenuOnSelect={false}
            controlShouldRenderValue={false}
            components={{
              DropdownIndicator: null,
              IndicatorSeperator: null,
              MultiValue: null,
              MultiValueContainer: null,
              MultiValueLabel: null,
              MultiValueRemove: null,
              Option,
            }}
            defaultValue={defaultValue}
            hideSelectedOptions={false}
            isClearable={false}
            isDisabled={disabled}
            isMulti={true}
            isSearchable={false}
            menuIsOpen={true}
            onBlur={handleOnBlur}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            options={isSorted ? options.sort(sortOptions) : options}
            styles={SelectMiniMultiStyling(theme)}
            tabSelectsValue={false}
            value={selectedItems}
          />

          <SelectMiniBlanket onClick={handleBlanketClick} />
        </SelectMiniMenu>
      )}
    </SelectMiniDropdown>
  );
};
